import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { parseVideoUrl } from "@/lib/videoEmbed";
import { Loader2, Clapperboard, ArrowLeft, Eye, ExternalLink, Trash2, Play } from "lucide-react";
import { toast } from "sonner";

const GENRE_LABEL = {
  nollywood: "Nollywood", drama: "Drama", comedy: "Comedy", action: "Action",
  romance: "Romance", thriller: "Thriller", epic: "Epic", documentary: "Documentary",
  kids: "Kids", music: "Music", other: "Other",
};

const TrailerPlayer = ({ url }) => {
  const info = parseVideoUrl(url);
  if (info.kind === "youtube" || info.kind === "vimeo") {
    return (
      <iframe
        src={info.embedSrc}
        title="Trailer"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="w-full h-full"
        data-testid="movie-trailer-iframe"
      />
    );
  }
  if (info.kind === "file") {
    return (
      <video src={info.embedSrc} controls className="w-full h-full" data-testid="movie-trailer-video" />
    );
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="w-full h-full flex flex-col items-center justify-center gap-2 text-white/70 hover:text-white"
      data-testid="movie-trailer-link"
    >
      <Play className="w-10 h-10" />
      <span className="text-sm">Open trailer in a new tab</span>
    </a>
  );
};

const MovieDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [movie, setMovie] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get(`/movies/${id}`)
      .then((r) => setMovie(r.data))
      .catch(() => toast.error("Movie not found"))
      .finally(() => setLoading(false));
  }, [id]);

  const remove = async () => {
    if (!window.confirm("Remove this movie? It will no longer be visible.")) return;
    try {
      await api.delete(`/movies/${id}`);
      toast.success("Removed");
      navigate("/movies");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0A0F] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-white/40" />
      </div>
    );
  }
  if (!movie) {
    return (
      <div className="min-h-screen bg-[#0B0A0F] flex items-center justify-center px-5 text-white">
        <div className="text-center">
          <Clapperboard className="w-12 h-12 mx-auto text-white/25 mb-3" />
          <div className="font-semibold">Movie not found</div>
          <Link to="/movies" className="text-sm text-white/50 underline mt-2 inline-block">
            Back to all movies
          </Link>
        </div>
      </div>
    );
  }

  const canManage = movie.is_owner || user?.role === "admin";

  return (
    <div className="min-h-screen bg-[#0B0A0F] text-white pb-24" data-testid="movie-detail-page">
      <div className="max-w-5xl mx-auto px-5 py-6">
        <div className="flex items-center justify-between mb-4">
          <Link to="/movies" className="inline-flex items-center gap-1 text-xs text-white/50 hover:text-white">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to movies
          </Link>
          {canManage && (
            <button
              onClick={remove}
              data-testid="movie-delete-btn"
              className="inline-flex items-center gap-1 text-xs text-[#F26D5B] hover:text-red-400"
            >
              <Trash2 className="w-3.5 h-3.5" /> Remove
            </button>
          )}
        </div>

        {/* Trailer */}
        <div className="rounded-2xl overflow-hidden border border-white/10 bg-black aspect-video">
          <TrailerPlayer url={movie.trailer_url} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-8">
          {/* Poster + watch CTA */}
          <div className="md:col-span-1">
            <div className="aspect-[2/3] rounded-2xl overflow-hidden bg-white/5 border border-white/10">
              {movie.poster_url ? (
                <img src={movie.poster_url} alt={movie.title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white/20">
                  <Clapperboard className="w-14 h-14" />
                </div>
              )}
            </div>
            {(movie.watch_options || []).length > 0 && (
              <div className="mt-4 space-y-2" data-testid="movie-watch-options">
                <div className="text-xs uppercase tracking-wider text-white/50 font-semibold">Where to watch</div>
                {movie.watch_options.map((o, i) => {
                  const badge =
                    o.access === "free"
                      ? { label: "FREE", cls: "bg-[#3E7D57] text-white" }
                      : o.access === "rent"
                      ? { label: "RENT", cls: "bg-[#B57A1E] text-white" }
                      : { label: "BUY", cls: "bg-[#B01E28] text-white" };
                  return (
                    <a
                      key={i}
                      href={o.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`movie-watch-cta-${i}`}
                      className="flex items-center gap-3 px-4 py-3 rounded-xl border border-white/12 bg-white/[0.04] hover:border-[#E4B851]/60 hover:bg-white/[0.07] transition-colors"
                    >
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${badge.cls}`}>{badge.label}</span>
                      <span className="text-sm font-semibold text-white flex-1 truncate">{o.platform}</span>
                      {o.note && <span className="text-xs text-white/60">{o.note}</span>}
                      <ExternalLink className="w-4 h-4 text-white/50" />
                    </a>
                  );
                })}
              </div>
            )}
            <p className="mt-3 text-[11px] text-white/40 text-center">
              Full film opens on the filmmaker's chosen platform.
            </p>
          </div>

          {/* Details */}
          <div className="md:col-span-2">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-[11px] uppercase tracking-wider font-semibold text-[#E4B851] mb-3">
              {GENRE_LABEL[movie.genre] || movie.genre}
            </div>
            <h1 className="font-display text-3xl sm:text-4xl tracking-tight leading-tight" data-testid="movie-detail-title">
              {movie.title}
            </h1>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-3 text-sm text-white/60">
              <span>{movie.country}</span>
              {movie.year ? <span>· {movie.year}</span> : null}
              {movie.language ? <span>· {movie.language}</span> : null}
              {movie.runtime_min ? <span>· {movie.runtime_min} min</span> : null}
              <span className="inline-flex items-center gap-1">
                · <Eye className="w-3.5 h-3.5" /> {movie.view_count}
              </span>
            </div>

            <div className="mt-6">
              <h2 className="text-sm uppercase tracking-wider text-white/50 font-semibold mb-2">Synopsis</h2>
              <p className="text-white/80 leading-relaxed whitespace-pre-wrap" data-testid="movie-detail-synopsis">
                {movie.synopsis}
              </p>
            </div>

            {(movie.director || movie.cast) && (
              <div className="mt-6 space-y-2 text-sm">
                {movie.director && (
                  <div><span className="text-white/50">Director: </span><span className="text-white/90">{movie.director}</span></div>
                )}
                {movie.cast && (
                  <div><span className="text-white/50">Cast: </span><span className="text-white/90">{movie.cast}</span></div>
                )}
              </div>
            )}

            <div className="mt-6 text-xs text-white/40">
              Submitted by {movie.submitter_name}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MovieDetail;
